import type { FunctionReturnType } from "convex/server";
import type { api } from "../../../convex/_generated/api";

/** Engine snapshot as returned by api.engine.getEngineState (non-null branch). */
export type EngineSnapshot = NonNullable<FunctionReturnType<typeof api.engine.getEngineState>>;

export type ExpenditurePoint = FunctionReturnType<typeof api.engine.getExpenditureHistory>[number];

export type JourneySummary = NonNullable<FunctionReturnType<typeof api.engine.getJourneySummary>>;
export type WeightChartData = FunctionReturnType<typeof api.engine.getWeightChartData>;
export type WeightChartPoint = WeightChartData["points"][number];

/** Chart row with the prediction band collapsed into a [min, max] range for recharts. */
export interface WeightChartRow extends WeightChartPoint {
  readonly predictedRange: [number, number] | null;
}

/** getWeeklyCheckins returns v.any() rows; this is the documented shape. */
export interface WeeklyCheckinRow {
  readonly weekStartDate: number;
  readonly previousTargetKcal: number;
  readonly newTargetKcal: number;
  readonly adjustmentKcal: number;
  readonly paused: boolean;
  readonly adjusted: boolean;
  readonly explanation: string;
  readonly createdAt: number;
}

export const WEIGHT_RANGES = [
  { id: "7d", label: "7D", days: 7 },
  { id: "30d", label: "30D", days: 30 },
  { id: "60d", label: "60D", days: 60 },
  { id: "90d", label: "90D", days: 90 },
  { id: "all", label: "All", days: 365 },
] as const;

export type WeightRangeId = (typeof WEIGHT_RANGES)[number]["id"];

export function resolveRangeDays(rangeId: WeightRangeId): number {
  return WEIGHT_RANGES.find((range) => range.id === rangeId)?.days ?? 60;
}

export function toChartRows(points: readonly WeightChartPoint[]): WeightChartRow[] {
  return points.map((point) => ({
    ...point,
    predictedRange:
      point.predictedMinKg !== null && point.predictedMaxKg !== null
        ? [point.predictedMinKg, point.predictedMaxKg]
        : null,
  }));
}

export function hasAnyWeighIn(points: readonly WeightChartPoint[]): boolean {
  return points.some((point) => point.rawKg !== null);
}

export type ProgressSummaryState = "losing" | "gaining" | "holding" | "insufficient";

export interface ProgressSummary {
  readonly state: ProgressSummaryState;
  readonly sentence: string;
}

const HOLDING_THRESHOLD_KG_PER_WEEK = 0.05;
const TARGET_CLOSE_TOLERANCE_KG_PER_WEEK = 0.15;

/**
 * Plain-language weekly status from the engine drift (actual kg/week) and the
 * plan's target weekly change. Sign-aware (losing/gaining/holding) and compares
 * |drift| against |weeklyChangeKg|. Null drift means the engine has too little
 * data, so the user gets guidance copy instead of a rate.
 */
export function buildProgressSummary(input: {
  readonly driftKgPerWeek: number | null;
  readonly weeklyChangeKg: number | null;
}): ProgressSummary {
  if (input.driftKgPerWeek === null) {
    return {
      state: "insufficient",
      sentence:
        "Not enough data yet. Log your weight regularly and complete your daily tracking to unlock your weekly progress summary.",
    };
  }
  const drift = Math.round(input.driftKgPerWeek * 10) / 10;
  const absDrift = Math.abs(drift);
  const targetAbs =
    input.weeklyChangeKg === null ? null : Math.round(Math.abs(input.weeklyChangeKg) * 10) / 10;

  let targetClause = "";
  if (targetAbs !== null && targetAbs > 0) {
    const delta = Math.abs(absDrift - targetAbs);
    targetClause =
      delta <= TARGET_CLOSE_TOLERANCE_KG_PER_WEEK
        ? `, close to your ${targetAbs.toFixed(1)} kg target`
        : absDrift < targetAbs
          ? `, slower than your ${targetAbs.toFixed(1)} kg target`
          : `, faster than your ${targetAbs.toFixed(1)} kg target`;
  }

  if (absDrift < HOLDING_THRESHOLD_KG_PER_WEEK) {
    if (targetAbs === null) {
      return { state: "holding", sentence: "Your weight is holding steady." };
    }
    if (targetAbs === 0) {
      return { state: "holding", sentence: "Your weight is holding steady, on target." };
    }
    return { state: "holding", sentence: `Your weight is holding steady${targetClause}.` };
  }
  const state: ProgressSummaryState = drift < 0 ? "losing" : "gaining";
  return {
    state,
    sentence: `You are ${state} approximately ${absDrift.toFixed(1)} kg per week${targetClause}.`,
  };
}

/** Signed weekly rate, e.g. "−0.4 kg/week" / "+0.3 kg/week" / "0 kg/week". */
export function formatWeeklyChange(kg: number | null): string {
  if (kg === null) return "—";
  const rounded = Math.round(kg * 10) / 10;
  if (rounded === 0) return "0 kg/week";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}${Math.abs(rounded).toFixed(1)} kg/week`;
}

/** Timeline range, e.g. "≈ 8–11 weeks to goal"; null when the engine has no estimate. */
export function formatWeeksRange(weeksMin: number | null, weeksMax: number | null): string | null {
  if (weeksMin === null || weeksMax === null) return null;
  const lo = Math.max(0, Math.round(Math.min(weeksMin, weeksMax)));
  const hi = Math.max(0, Math.round(Math.max(weeksMin, weeksMax)));
  if (lo === hi) return `≈ ${lo} week${lo === 1 ? "" : "s"} to goal`;
  return `≈ ${lo}–${hi} weeks to goal`;
}

export function formatShortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Goal-date window, e.g. "Sep 3, 2026 – Nov 12, 2026"; null when unknown. */
export function formatGoalDateRange(minMs: number | null, maxMs: number | null): string | null {
  if (minMs === null || maxMs === null) return null;
  if (minMs === maxMs) return formatShortDate(minMs);
  return `${formatShortDate(minMs)} – ${formatShortDate(maxMs)}`;
}

/** Progress toward the weight goal in percent (0–100); null when not computable. */
export function goalProgressPercent(
  startKg: number | null | undefined,
  currentKg: number | null,
  targetKg: number | null | undefined,
): number | null {
  if (startKg === null || startKg === undefined || currentKg === null || targetKg === null || targetKg === undefined) {
    return null;
  }
  const total = targetKg - startKg;
  if (total === 0) return currentKg === targetKg ? 100 : 0;
  const done = (currentKg - startKg) / total;
  return Math.round(Math.max(0, Math.min(1, done)) * 100);
}

export function expenditureLabel(kind: string): string {
  return kind === "personalised" ? "Personalised expenditure" : "Estimated expenditure (initial)";
}

export function expenditureBadge(kind: string): string {
  return kind === "personalised" ? "Personalised" : "Initial estimate";
}

export function confidenceLabel(level: string): string {
  switch (level) {
    case "low":
      return "Low confidence";
    case "moderate":
      return "Moderate";
    case "high":
      return "High";
    default:
      return "Learning";
  }
}

export function confidenceChipClass(level: string): string {
  switch (level) {
    case "low":
      return "bg-amber-100 text-amber-700";
    case "moderate":
      return "bg-teal-100 text-teal-700";
    case "high":
      return "bg-green-100 text-green-700";
    default:
      return "bg-muted text-text-muted";
  }
}

export function dataQualityDotClass(quality: string): string {
  switch (quality) {
    case "low":
      return "bg-orange-500";
    case "moderate":
      return "bg-teal-600";
    case "high":
      return "bg-green-600";
    default:
      return "bg-amber-500";
  }
}

/** 2–3 human reasons for the current data-quality state. */
export function buildDataQualityReasons(state: EngineSnapshot): string[] {
  const reasons: string[] = [];
  if (state.trendConfidence === "learning") {
    reasons.push("Weight trend is still calibrating — log weigh-ins regularly.");
  } else if (state.trendConfidence === "low") {
    reasons.push("Weight trend has few observations, so confidence is low.");
  } else if (state.trendConfidence === "high") {
    reasons.push("Weight trend is well established from your weigh-ins.");
  }
  if (state.expenditureKind === "initial") {
    reasons.push("Expenditure is an initial estimate from your profile details.");
  } else {
    reasons.push("Expenditure is personalised from your logged food and weight.");
  }
  if (state.weeksOffTrack > 0) {
    reasons.push(
      `${state.weeksOffTrack} recent week${state.weeksOffTrack === 1 ? "" : "s"} tracked off target.`,
    );
  }
  return reasons.slice(0, 3);
}

export function dataQualityTip(state: EngineSnapshot): string {
  if (state.dataQuality === "high") {
    return "Keep logging consistently to maintain accuracy.";
  }
  return "Log meals completely for 4+ days this week to personalise your plan.";
}

export const DAY_STATUS_ORDER = ["complete", "partial", "fasting", "not_logged"] as const;
export type DayStatusValue = (typeof DAY_STATUS_ORDER)[number];

/** Cycle order: Complete → Partial → Fasting → Not logged → Complete. */
export function nextDayStatus(current: string): DayStatusValue {
  const index = (DAY_STATUS_ORDER as readonly string[]).indexOf(current);
  const next = DAY_STATUS_ORDER[(index + 1) % DAY_STATUS_ORDER.length];
  return next ?? "complete";
}

export function dayStatusLabel(status: string): string {
  switch (status) {
    case "complete":
      return "Complete";
    case "partial":
      return "Partial";
    case "fasting":
      return "Fasting";
    default:
      return "Not logged";
  }
}

export function dayStatusChipClass(status: string): string {
  switch (status) {
    case "complete":
      return "bg-green-100 text-green-700";
    case "partial":
      return "bg-amber-100 text-amber-700";
    case "fasting":
      return "bg-teal-100 text-teal-700";
    default:
      return "bg-muted text-text-muted";
  }
}

export function dayStatusDotClass(status: string): string {
  switch (status) {
    case "complete":
      return "bg-green-500";
    case "partial":
      return "bg-amber-500";
    case "fasting":
      return "bg-teal-600";
    default:
      return "bg-text-muted/50";
  }
}

/** Signed kcal adjustment, e.g. "+150 kcal" / "−80 kcal". */
export function formatAdjustmentKcal(kcal: number): string {
  const rounded = Math.round(kcal);
  if (rounded === 0) return "±0 kcal";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}${Math.abs(rounded)} kcal`;
}
